import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    getCreditOrderHistory,
    getCreditGenerationHistory,
    OrderHistoryResponse,
    GenerationHistoryResponse
} from '../../services/creditTransactionService';
import TransactionTabs from '../credit-history/TransactionTabs';
import CreditSummary from '../credit-history/CreditSummary';
import OrderHistoryTab from '../credit-history/OrderHistoryTab';
import GenerationHistoryTab from '../credit-history/GenerationHistoryTab';
import TransactionFilters from '../credit-history/TransactionFilters';
import CustomSelect from '../ui/CustomSelect';

const CreditHistory: React.FC = () => {
    const { currentUser, userCredits } = useAuth();
    const [activeTab, setActiveTab] = useState<'purchases' | 'usage'>('usage');
    const [orderHistory, setOrderHistory] = useState<OrderHistoryResponse | null>(null);
    const [generationHistory, setGenerationHistory] = useState<GenerationHistoryResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filter states
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [timelineFilter, setTimelineFilter] = useState<'all' | '7' | '30' | '90'>('all');

    useEffect(() => {
        if (currentUser?.uid) {
            fetchData();
        }
    }, [currentUser]);

    const handleTimelineChange = (timeline: 'all' | '7' | '30' | '90') => {
        setTimelineFilter(timeline);
        if (timeline === 'all') {
            setDateRange({ start: null, end: null });
        } else {
            const days = parseInt(timeline);
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(endDate.getDate() - days);
            setDateRange({ start: startDate, end: endDate });
        }
    };

    const fetchData = async () => {
        if (!currentUser?.uid) return;

        setIsLoading(true);
        setError(null);

        try {
            const [orderData, generationData] = await Promise.all([
                getCreditOrderHistory(currentUser.uid),
                getCreditGenerationHistory(currentUser.uid)
            ]);

            setOrderHistory(orderData);
            setGenerationHistory(generationData);
        } catch (err) {
            setError(`Failed to load credit history: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Get current balance from the API response or fall back to userCredits
    const currentBalance = orderHistory?.current_balance ?? generationHistory?.current_balance ?? userCredits;

    // Helper function to check if a generation was successful
    const isGenerationSuccessful = (transaction: any): boolean => {
        const details = transaction.details;
        if (!details) return false;

        // For video generations, check if videoUrls is not empty
        if (transaction.generation_type === 'video-generation' || transaction.generation_type === 'video_generation') {
            return details.videoUrls && details.videoUrls.length > 0;
        }

        // For image generations (photoshoot, try-on, fashion_extractor, ai-poses, background-removal, ai-workflow), check if imageUrls is not empty
        return details.imageUrls && details.imageUrls.length > 0;
    };

    // Calculate summary stats excluding failed generations
    const summaryStats = useMemo(() => {
        // Recalculate total used credits excluding failed generations
        const successfulTransactions = generationHistory?.generation_history?.filter(isGenerationSuccessful) || [];
        const actualTotalUsed = successfulTransactions.reduce((sum, transaction) => sum + transaction.credits_used, 0);
        const actualTotalGenerations = successfulTransactions.length;

        return {
            totalPurchased: orderHistory?.total_credits_purchased || 0,
            totalUsed: actualTotalUsed,
            totalOrders: orderHistory?.total_orders || 0,
            totalGenerations: actualTotalGenerations,
            usageByType: generationHistory?.usage_by_type || {}
        };
    }, [orderHistory, generationHistory]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto"></div>
                    <p className="mt-4 text-text-description">Loading credit history...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
                    <p className="text-text-heading">{error}</p>
                    <button
                        onClick={fetchData}
                        className="mt-4 bg-secondary-500 text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 md:p-6 max-w-6xl mx-auto w-full overflow-x-hidden">
            {/* Header */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-text-heading">Credit History</h1>
                    <p className="text-text-description mt-2">Track your credit purchases and usage</p>
                </div>

                {/* Timeline Filter */}
                <div className="mt-4 md:mt-0">
                    <CustomSelect
                        value={timelineFilter}
                        onChange={(value) => handleTimelineChange(value as 'all' | '7' | '30' | '90')}
                        options={[
                            { value: 'all', label: 'All Time' },
                            { value: '7', label: 'Last 7 Days' },
                            { value: '30', label: 'Last 30 Days' },
                            { value: '90', label: 'Last 90 Days' }
                        ]}
                        className="w-full min-w-[120px]"
                    />
                </div>
            </div>

            {/* Credit Summary */}
            <CreditSummary
                currentBalance={currentBalance}
                summaryStats={summaryStats}
            />

            {/* Filters */}
            <TransactionFilters
                dateRange={dateRange}
                setDateRange={setDateRange}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
            />

            {/* Tabs */}
            <TransactionTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
            />

            {/* Tab Content */}
            <div className="mt-6 w-full">
                {activeTab === 'purchases' ? (
                    <OrderHistoryTab
                        orderHistory={orderHistory?.order_history || []}
                        dateRange={dateRange}
                        searchTerm={searchTerm}
                    />
                ) : (
                    <GenerationHistoryTab
                        generationHistory={generationHistory?.generation_history || []}
                        usageByType={generationHistory?.usage_by_type || {}}
                        dateRange={dateRange}
                        searchTerm={searchTerm}
                    />
                )}
            </div>
        </div>
    );
};

export default CreditHistory; 